import {DatePipe, DecimalPipe, NgTemplateOutlet} from '@angular/common'
import {Component, computed, inject, input, OnInit, signal} from '@angular/core'
import {FormControl, ReactiveFormsModule} from '@angular/forms'
import {MatButton, MatIconButton} from '@angular/material/button'
import {MatDialog} from '@angular/material/dialog'
import {MatFormField} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {MatPaginator} from '@angular/material/paginator'
import {MatSort, MatSortHeader} from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table'
import {MatTooltip} from '@angular/material/tooltip'
import {RouterLink} from '@angular/router'
import {IApplicationSummary} from '@sparbanken-syd/loan-backend'
import {ConfigService} from '@sparbanken-syd/sparbanken-syd-bankid'
import {
  BaseTable,
  ConfirmComponent,
  PersonnummerValidatorDirective,
  WaitComponent
} from '@sparbanken-syd/sparbanken-syd-theme'
import {filter, finalize, startWith, switchMap} from 'rxjs'
import {StatusIndicatorComponent} from '../../../components/status-indicator/status-indicator.component'
import {LoanService} from '../../../services/loan.service'
import {RoleHelper} from '../../../services/role-helper'
import {UserDocumentsService} from '../../../services/user-documents.service'
import {TViewType} from '../admin-content.component'
import {DelegateComponent} from '../delegate/delegate.component'
import {FindDocumentsComponent} from '../find-documents/find-documents.component'
import {ShowDocumentsComponent} from '../show-documents/show-documents.component'

@Component({
  selector: 'spb-promise-list',
  templateUrl: './promise-list.component.html',
  styleUrls: ['./promise-list.component.scss'],
  imports: [
    MatButton,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    RouterLink,
    StatusIndicatorComponent,
    MatTooltip,
    MatIcon,
    NgTemplateOutlet,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    MatPaginator,
    DecimalPipe,
    DatePipe,
    PersonnummerValidatorDirective,
    MatIconButton
  ]
})
export class PromiseListComponent extends BaseTable<IApplicationSummary> implements OnInit {
  public view = input.required<TViewType>()

  public pkg = signal<string[]>([])

  public columnsToDisplay = computed(() => {
    if (this.view() === 'contact') {
      return ['dateOfIssue', 'timeOfIssue', 'applicantFull', 'applicantContact', 'kalp']
    }

    const cols = ['dateOfIssue', 'applicantName', 'status', 'approvedAmount', 'kalp', 'assignee', 'delete']

    if (this.helper.canShowDocuments()) {
      return [...cols, 'documents']
    }

    return cols
  })

  public filterCtrl = new FormControl<string>('', {nonNullable: true})
  public readonlySub = new FormControl<string>('', {nonNullable: true})

  public readonly helper = inject(RoleHelper)
  protected readonly configService = inject(ConfigService)
  private readonly loanService = inject(LoanService)
  private readonly userDocumentsService = inject(UserDocumentsService)
  private readonly dialog = inject(MatDialog)

  public ngOnInit() {
    this.filterCtrl.valueChanges.pipe(
      startWith('')
    ).subscribe(v => {
      this.items.filter = v.trim().toLowerCase()
    })
    if (this.helper.canAdminClassic()) {
      this.update()
    }
  }

  public getReadOnly() {
    const waitRef = this.dialog.open(WaitComponent, {
      data: {title: 'Laddar', progress: true}
    })

    this.loanService.listReadOnly(this.readonlySub.value).subscribe(
      value => {
        this.items.data = value
        this.showDocumentColumn(value)
        waitRef.close()
      }
    )
  }

  public update() {
    const waitRef = this.dialog.open(WaitComponent, {
      data: {title: 'Laddar...', progress: true}
    })

    this.loanService.list()
      .subscribe(loanPromises => {
        this.items.data = loanPromises
        this.showDocumentColumn(loanPromises)
        waitRef.close()
      })
  }

  public checkLoanPromise(loanPromise: IApplicationSummary) {
    return {
      applicant: this.pkg().includes(loanPromise.applicantPersonNummer),
      coApplicant: this.pkg().includes(loanPromise.coApplicantId)
    }
  }

  public findPkg() {
    if (this.helper.canAdminClassic()) {
      this.dialog.open(FindDocumentsComponent, {})
    }
  }

  public remove(id: string, name: string) {

    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: {
        title: 'Ta bort lånelöfte?',
        text: `Är du säker på att du vill ta bort lånelöfte för ${name}?`,
        html: `<br><b>OBS! Åtgärden kan inte ångras</b>`,
        confirmText: 'Ja',
        cancelText: 'Nej'
      }
    })
    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => {
          const waitRef = this.dialog.open(WaitComponent, {
            data: {title: 'Raderar', progress: true}
          })
          return this.loanService.delete(id).pipe(finalize(() => waitRef.close()))
        })
      )
      .subscribe(() => this.update())
  }

  public getDoc(sub: string) {
    const waitRef = this.dialog.open(WaitComponent, {
      data: {title: 'Laddar...', progress: true}
    })

    this.userDocumentsService.getPackage(sub).pipe(
      finalize(() => waitRef.close())
    ).subscribe(
      document => {
        this.dialog.open(ShowDocumentsComponent, {
          data: {
            documents: document.savedDocuments,
            sub
          }
        })
      }
    )
  }

  public delegate(application: IApplicationSummary) {
    const dialogRef = this.dialog.open(DelegateComponent, {
      data: {
        application,
        offices: this.loanService.offices()
      },
      disableClose: true,
      width: '400px'
    })
    dialogRef.afterClosed().pipe(filter(Boolean)).subscribe(() => {
      // Refresh if the delegate was saved
      this.update()
    })
  }

  private showDocumentColumn(loanPromises: IApplicationSummary[]) {
    const subs = new Set<string>()
    // This will send occasional nulls and undefined but that
    // should be handled by the backend.
    loanPromises.forEach((l) => {
      subs.add(l.applicantPersonNummer)
      subs.add(l.coApplicantId)
    })

    if (this.helper.canShowDocuments()) {
      this.userDocumentsService.getAllPackages([...subs])
        .pipe(filter(Boolean))
        .subscribe(
          pkg => {
            this.pkg.set(pkg)
          }
        )
    }
  }
}